import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import CampsiteMap, { SHAPES, SIZES } from "@/components/dashboard/CampsiteMap";
import { Panel } from "@/pages/shared";
import ZonesPanel from "@/components/ZonesPanel";

const KINDS = ["pitch", "bungalow", "cabin"];

export default function KaartUpdate() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [pitches, setPitches] = useState([]);
  const [guests, setGuests] = useState([]);
  const [form, setForm] = useState({ zone: "", number: "", number_to: "", label: "", kind: "pitch", shape: "circle", size: 2 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p, g] = await Promise.all([api.get("/dashboard/stats"), api.get("/pitches"), api.get("/guests")]);
      setStats(s.data); setPitches(p.data); setGuests(g.data);
      // zone enkel voorstellen zolang er nog niets gekozen is
      setForm((f) => (f.zone || !s.data.zones?.length ? f : { ...f, zone: s.data.zones[0].code }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updatePitch = async (id, body) => {
    try { await api.patch(`/pitches/${id}`, body); await load(); toast.success(t("saved")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.label.trim()) {
        // een eigen naam hoort bij één plaats
        await api.post("/pitches", { ...form, number: Number(form.number), size: Number(form.size), label: form.label.trim() });
        toast.success(t("pitch_created"));
      } else {
        const { data } = await api.post("/pitches/bulk", {
          zone: form.zone, kind: form.kind, shape: form.shape, size: Number(form.size),
          number_from: Number(form.number),
          number_to: form.number_to === "" ? null : Number(form.number_to),
        });
        toast.success(data.created === 1 ? t("pitch_created") : t("pitches_created").replace("{n}", data.created));
        if (data.skipped.length) toast.info(`${t("pitches_skipped").replace("{n}", data.skipped.length)}: ${data.skipped.join(", ")}`);
      }
      setForm((f) => ({ ...f, number: "", number_to: "", label: "" }));
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const zones = stats?.zones || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="outline" size="sm"><Link to="/admin" data-testid="back-to-admin"><ArrowLeft className="h-4 w-4 mr-1" />{t("nav_admin")}</Link></Button>
        <h1 className="font-display font-bold text-2xl tracking-tight text-foreground flex items-center gap-2"><MapPinned className="h-5 w-5 text-primary" />{t("map_update")}</h1>
      </div>

      <ZonesPanel zones={zones} pitches={pitches} onReload={load} />

      <Panel title={t("new_pitch")} testid="new-pitch-panel">
        <form onSubmit={create} className="p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 items-end">
          <div>
            <Label className="mb-1.5 block">{t("zone")}</Label>
            <Select value={form.zone} onValueChange={(v) => setForm({ ...form, zone: v })}>
              <SelectTrigger data-testid="new-pitch-zone"><SelectValue placeholder={t("zone")} /></SelectTrigger>
              <SelectContent>{zones.map((z) => <SelectItem key={z.code} value={z.code}>{z.code} · {z.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">{t("number_from")}</Label>
            <Input type="number" min="1" required value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="new-pitch-number" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("number_to")}</Label>
            <Input type="number" min="1" value={form.number_to} disabled={!!form.label.trim()}
              placeholder={form.number || ""} onChange={(e) => setForm({ ...form, number_to: e.target.value })} data-testid="new-pitch-number-to" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("label_optional")}</Label>
            <Input value={form.label} disabled={!!form.number_to} placeholder={form.zone ? `${form.zone}${form.number || ""}X` : ""}
              onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="new-pitch-label" />
            <p className="mt-1 text-[11px] text-muted-foreground">{t("label_optional_hint")}</p>
          </div>
          <div>
            <Label className="mb-1.5 block">{t("type")}</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="new-pitch-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{t(`kind_${k}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">{t("shape")}</Label>
            <div className="flex gap-1.5">
              {SHAPES.map((s) => (
                <button type="button" key={s} onClick={() => setForm({ ...form, shape: s })} title={t(`shape_${s}`)} data-testid={`shape-${s}`}
                  className={`h-9 w-9 grid place-items-center rounded-md border transition-colors ${form.shape === s ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`}>
                  <span className={`block h-3.5 w-3.5 bg-slate-700 ${s === "circle" ? "rounded-full" : "rounded-[2px]"} ${s === "diamond" ? "rotate-45" : ""}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">{t("size")}</Label>
            <div className="flex gap-1.5">
              {SIZES.map((sz) => (
                <button type="button" key={sz} onClick={() => setForm({ ...form, size: sz })} title={t(`size_${sz}`)} data-testid={`new-pitch-size-${sz}`}
                  className={`h-9 px-2.5 flex items-center gap-1.5 rounded-md border transition-colors ${form.size === sz ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`}>
                  <span className={`block bg-slate-700 ${form.shape === "circle" ? "rounded-full" : "rounded-[2px]"} ${form.shape === "diamond" ? "rotate-45" : ""}`}
                    style={{ height: 6 + sz * 4, width: 6 + sz * 4 }} />
                  <span className="text-[11px] font-medium">{t(`size_${sz}`)}</span>
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={busy || !form.zone} data-testid="new-pitch-submit"><Plus className="h-4 w-4 mr-1" />{t("add")}</Button>
          <p className="col-span-2 md:col-span-3 lg:col-span-7 text-[11px] text-muted-foreground" data-testid="new-pitch-hint">{t("pitch_bulk_hint")}</p>
        </form>
      </Panel>

      <div className="bg-card rounded-xl border border-border p-5">
        <CampsiteMap pitches={pitches} stats={stats} guests={guests} onUpdate={updatePitch} onReload={load} editable defaultZoom={3} />
      </div>
    </div>
  );
}
